export interface ModalAction<T> {
  label: string;
  value: T;
  disabled?: boolean;
  subtle?: boolean;
}

/**
 * One consent checkbox. `label` may contain the literal "{link}" token: when a
 * `link` is also given, that token is replaced inline by a clickable link
 * (otherwise the label renders as plain text). `requireLinkOpened` starts the box
 * disabled until its link is clicked, forcing the user to open the linked content
 * (e.g. the Terms) before they can consent.
 */
export interface ModalCheckbox {
  label: string;
  checked?: boolean;
  requireLinkOpened?: boolean;
  disabled?: boolean;
  title?: string;
  link?: { label: string; onClick: () => void };
}

/**
 * Generic choice modal: a centered .modal-box over a .modal-overlay with a
 * title, an optional message body (newlines preserved), and one button per
 * action. Clicking a button removes the overlay and resolves onChoice with that
 * action's value. Shared markup with the group-delete prompt so styling stays
 * in one place.
 */
export interface ActionModalOptions<T> {
  checkbox?: ModalCheckbox | ModalCheckbox[];
  gates?: readonly T[];
  previewSrc?: string;
  fields?: { label: string; value: string }[];
  textInput?: { label: string; placeholder?: string; value?: string };
  numberInput?: { label: string; value: number; min: number; max: number };
  vertical?: boolean;
  dismissable?: { label: string };
}

/**
 * gates lists action values that require every checkbox to be ticked: those
 * buttons start disabled and toggle as the boxes are checked/unchecked, so an
 * upload cannot proceed without each consent tick. An action's own `disabled`
 * flag always wins (e.g. a not-signed-in upload stays disabled regardless of the
 * boxes). The `checked` value passed to onChoice is true only when all checkboxes
 * are ticked; `checks` carries each checkbox's state in declaration order so a
 * modal with several independent toggles (e.g. public + anon-gate) can read them
 * separately.
 */
export function showActionModal<T>(
  title: string,
  message: string | null,
  actions: readonly ModalAction<T>[],
  onChoice: (value: T, checked: boolean, text: string, checks: boolean[], num: number) => void,
  options?: ActionModalOptions<T>,
): void {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  const box = document.createElement("div");
  box.className = "modal-box";
  const titleEl = document.createElement("div");
  titleEl.className = "modal-title";
  titleEl.textContent = title;
  box.appendChild(titleEl);
  if (options?.dismissable !== undefined) {
    const closeBtn = document.createElement("button");
    closeBtn.className = "modal-close";
    closeBtn.textContent = "✕";
    closeBtn.setAttribute("aria-label", options.dismissable.label);
    closeBtn.addEventListener("click", (): void => {
      overlay.remove();
    });
    box.appendChild(closeBtn);
  }
  if (message !== null) {
    const messageEl = document.createElement("div");
    messageEl.className = "modal-message";
    messageEl.textContent = message;
    box.appendChild(messageEl);
  }
  if (options?.fields !== undefined) {
    const list = document.createElement("div");
    list.className = "modal-fields";
    for (const f of options.fields) {
      const row = document.createElement("div");
      row.className = "modal-field-row";
      const label = document.createElement("span");
      label.className = "modal-field-label";
      label.textContent = f.label;
      const value = document.createElement("span");
      value.className = "modal-field-value";
      value.textContent = f.value;
      row.append(label, value);
      list.appendChild(row);
    }
    box.appendChild(list);
  }
  let textInputEl: HTMLInputElement | null = null;
  if (options?.textInput !== undefined) {
    const spec = options.textInput;
    const row = document.createElement("label");
    row.className = "modal-text-input";
    const label = document.createElement("span");
    label.className = "modal-text-input-label";
    label.textContent = spec.label;
    const input = document.createElement("input");
    input.type = "text";
    input.className = "text-input";
    if (spec.placeholder !== undefined) {
      input.placeholder = spec.placeholder;
    }
    input.value = spec.value ?? "";
    row.append(label, input);
    box.appendChild(row);
    textInputEl = input;
  }
  let numberInputEl: HTMLInputElement | null = null;
  const numberSpec = options?.numberInput;
  if (numberSpec !== undefined) {
    const row = document.createElement("label");
    row.className = "modal-text-input";
    const label = document.createElement("span");
    label.className = "modal-text-input-label";
    label.textContent = numberSpec.label;
    const input = document.createElement("input");
    input.type = "number";
    input.className = "text-input";
    input.min = String(numberSpec.min);
    input.max = String(numberSpec.max);
    input.step = "1";
    input.value = String(numberSpec.value);
    row.append(label, input);
    box.appendChild(row);
    numberInputEl = input;
  }
  const readNumber = (): number => {
    if (numberSpec === undefined) {
      return 0;
    }
    const parsed = Math.floor(Number(numberInputEl?.value));
    if (!Number.isFinite(parsed)) {
      return numberSpec.value;
    }
    return Math.max(numberSpec.min, Math.min(numberSpec.max, parsed));
  };
  if (options?.previewSrc !== undefined) {
    const previewEl = document.createElement("img");
    previewEl.className = "modal-preview";
    previewEl.src = options.previewSrc;
    box.appendChild(previewEl);
  }
  const checkboxSpecs: ModalCheckbox[] =
    options?.checkbox === undefined ? [] : Array.isArray(options.checkbox) ? options.checkbox : [options.checkbox];
  const checkboxInputs: HTMLInputElement[] = [];
  for (const spec of checkboxSpecs) {
    const checkboxRow = document.createElement("label");
    checkboxRow.className = "modal-checkbox";
    if (spec.title !== undefined) {
      checkboxRow.title = spec.title;
    }
    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = spec.checked ?? false;
    input.disabled = spec.requireLinkOpened === true || spec.disabled === true;
    checkboxRow.appendChild(input);
    checkboxRow.appendChild(buildCheckboxLabel(spec, input));
    box.appendChild(checkboxRow);
    checkboxInputs.push(input);
  }
  const allChecked = (): boolean =>
    checkboxInputs.length > 0 && checkboxInputs.every((i: HTMLInputElement): boolean => i.checked);
  const gates = options?.gates;
  const gatedButtons: HTMLButtonElement[] = [];
  const actionsEl = document.createElement("div");
  actionsEl.className = options?.vertical === true ? "modal-actions modal-actions-vertical" : "modal-actions";
  for (const action of actions) {
    const btn = document.createElement("button");
    btn.textContent = action.label;
    if (action.subtle === true) {
      btn.classList.add("modal-btn-subtle");
    }
    const baseDisabled = action.disabled ?? false;
    const gated = gates !== undefined && gates.includes(action.value);
    btn.disabled = baseDisabled || (gated && !allChecked());
    if (gated && !baseDisabled) {
      gatedButtons.push(btn);
    }
    btn.addEventListener("click", (): void => {
      overlay.remove();
      onChoice(action.value, allChecked(), textInputEl?.value ?? "", checkboxInputs.map((i: HTMLInputElement): boolean => i.checked), readNumber());
    });
    actionsEl.appendChild(btn);
  }
  if (gatedButtons.length > 0) {
    const sync = (): void => {
      for (const btn of gatedButtons) {
        btn.disabled = !allChecked();
      }
    };
    for (const input of checkboxInputs) {
      input.addEventListener("change", sync);
    }
  }
  box.appendChild(actionsEl);
  overlay.appendChild(box);
  document.body.appendChild(overlay);
}

/**
 * Confirmation modal shown after a successful scene upload: the share URL in a
 * read-only field with a copy button that gives inline "copied" feedback, plus a
 * close button. Separate from showActionModal because copying must not dismiss
 * the modal — the user copies the URL and can still read it before closing.
 */
export function showUploadSuccessModal(opts: {
  title: string;
  urlLabel: string;
  url: string;
  copyLabel: string;
  copiedLabel: string;
  closeLabel: string;
  hideUrlLabel?: boolean;
  hideCloseButton?: boolean;
  dismissOnOverlayClick?: boolean;
}): void {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  const box = document.createElement("div");
  box.className = "modal-box";
  const titleEl = document.createElement("div");
  titleEl.className = "modal-title";
  titleEl.textContent = opts.title;
  const labelEl = document.createElement("div");
  labelEl.className = "modal-message";
  labelEl.textContent = opts.urlLabel;
  const urlRow = document.createElement("div");
  urlRow.className = "row";
  const urlInput = document.createElement("input");
  urlInput.type = "text";
  urlInput.readOnly = true;
  urlInput.className = "text-input";
  urlInput.value = opts.url;
  const copyBtn = document.createElement("button");
  copyBtn.className = "modal-copy-btn";
  copyBtn.title = opts.copyLabel;
  copyBtn.setAttribute("aria-label", opts.copyLabel);
  copyBtn.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" fill="currentColor"/></svg>`;
  const feedback = document.createElement("div");
  feedback.className = "row-value scene-status share-status modal-copy-feedback";
  copyBtn.addEventListener("click", (): void => {
    urlInput.select();
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(opts.url)
        .then((): void => {
          feedback.textContent = opts.copiedLabel;
        })
        .catch((): void => {});
    }
  });
  urlRow.append(urlInput, copyBtn);
  const actionsEl = document.createElement("div");
  actionsEl.className = "modal-actions";
  const closeBtn = document.createElement("button");
  closeBtn.textContent = opts.closeLabel;
  closeBtn.addEventListener("click", (): void => overlay.remove());
  if (opts.hideCloseButton !== true) {
    actionsEl.appendChild(closeBtn);
  }
  if (opts.hideUrlLabel === true) {
    labelEl.style.display = "none";
  }
  box.append(titleEl, labelEl, urlRow, feedback);
  if (opts.hideCloseButton !== true) {
    box.appendChild(actionsEl);
  }
  if (opts.dismissOnOverlayClick === true) {
    overlay.addEventListener("click", (ev: MouseEvent): void => {
      if (ev.target === overlay) {
        overlay.remove();
      }
    });
  }
  overlay.appendChild(box);
  document.body.appendChild(overlay);
}

/** Blocking progress overlay shown during async operations. Returns a handle to remove it. */
export function showWaitModal(title: string, message: string): { close(): void } {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  const box = document.createElement("div");
  box.className = "modal-box";
  const titleEl = document.createElement("div");
  titleEl.className = "modal-title";
  titleEl.textContent = title;
  box.appendChild(titleEl);
  if (message !== "") {
    const messageEl = document.createElement("div");
    messageEl.className = "modal-message";
    messageEl.textContent = message;
    box.appendChild(messageEl);
  }
  const spinner = document.createElement("div");
  spinner.className = "modal-wait-spinner";
  spinner.innerHTML = '<svg viewBox="0 0 50 50"><circle class="spinner-outline" cx="25" cy="25" r="20" /><circle class="spinner-arc" cx="25" cy="25" r="20" /></svg>';
  box.appendChild(spinner);
  overlay.appendChild(box);
  document.body.appendChild(overlay);
  return {
    close(): void {
      overlay.remove();
    },
  };
}

const CHECKBOX_LINK_TOKEN = "{link}";

/**
 * Builds the text content for a consent checkbox row. When the spec carries a
 * link and its label contains the "{link}" token, the token is replaced by an
 * inline link button (clicking it opens the linked content and enables the box,
 * stopping propagation so the surrounding label does not also toggle the box).
 */
function buildCheckboxLabel(spec: ModalCheckbox, input: HTMLInputElement): HTMLSpanElement {
  const content = document.createElement("span");
  content.className = "modal-checkbox-text";
  const link = spec.link;
  if (link === undefined || !spec.label.includes(CHECKBOX_LINK_TOKEN)) {
    content.textContent = spec.label;
    return content;
  }
  const [before, after] = spec.label.split(CHECKBOX_LINK_TOKEN);
  if (before !== "") {
    content.appendChild(document.createTextNode(before));
  }
  const linkEl = document.createElement("button");
  linkEl.className = "modal-link modal-link-inline";
  linkEl.textContent = link.label;
  const onClick = link.onClick;
  linkEl.addEventListener("click", (e: MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
    input.disabled = false;
  });
  content.appendChild(linkEl);
  if (after !== "") {
    content.appendChild(document.createTextNode(after));
  }
  return content;
}
